import { get, post, patch, getAllPages } from '@/api/rest'
import { post as rpcPost } from '@/helpers/api'
import {
	setMessages,
	setCursorsForBubble,
	setStartCursorForBubble,
	setStopCursorForBubble,
	getStopCursorForBubble,
	diffMembershipsForBubble,
	deleteCachedMessagesNotInSet,
	setChat,
	updateChat as storeUpdateChat,
	setStat,
	setUser,
	getMyUserId,
	setMembershipsSafe,
	setChats,
	setUsers,
	setStats,
	removeChatFromCategory,
} from '@/store'

export const fetchBubble = async (bubble_id, cancelToken) => {
	if (typeof bubble_id !== 'object') {
		bubble_id = { bubble_id }
	}
	const { data } = await rpcPost('/api/v2/bubble.info', bubble_id, cancelToken)
	return data
}

export const fetchChat = async (chatId, params = {}) => {
	const { data: chat } = await get(`/chats/${chatId}`, params)
	setChat(chat)
	return chat
}

export const fetchMessages = async (chatId, params = {}, config = {}) => {
	const resp = await get(`/chats/${chatId}/messages`, params, config)
	return {
		messages: resp.data.messages || [],
		parentMessages: resp.data.parent_messages || [],
		perPage: resp.meta?.per_page || 50,
		cursors: resp.cursors || {},
		next: resp.next,
		prev: resp.prev,
	}
}

export const fetchHistoryWithoutStore = async (payload, cancelToken) => {
	const {
		bubble_id,
		latest,
		oldest,
		resources,
		videosessions,
		mediatypes,
		polls,
		thread_id,
	} = payload

	const params = {}
	if (latest) params.before = latest
	if (oldest) params.after = oldest
	if (resources) params['filter[resources]'] = 1
	if (videosessions) params['filter[video_sessions]'] = 1
	if (mediatypes) params['filter[media_types]'] = mediatypes
	if (polls) params['filter[polls]'] = 1
	if (thread_id) params['filter[reply_chain]'] = thread_id

	const config = cancelToken ? { cancelToken: cancelToken.token } : {}
	const result = await fetchMessages(bubble_id, params, config)

	return {
		messages: result.messages,
		parentmessages: result.parentMessages,
		pagesize: result.perPage,
	}
}

export const fetchCenteredHistory = async (
	bubbleId,
	messageId,
	cancelToken
) => {
	try {
		const config = cancelToken ? { cancelToken: cancelToken.token } : {}
		const result = await fetchMessages(
			bubbleId,
			{ around: messageId, 'page[size]': 100 },
			config
		)

		setMessages(result.messages)
		setMessages(result.parentMessages)

		const oldestMsg = result.messages[result.messages.length - 1]
		const newestMsg = result.messages[0]
		if (oldestMsg && newestMsg) {
			setCursorsForBubble(bubbleId, {
				start: oldestMsg.id,
				stop: newestMsg.id,
			})
		}

		const olderMessages = result.messages.filter((m) => m.id <= messageId)
		const newerMessages = result.messages.filter((m) => m.id > messageId)
		return {
			older: {
				messages: olderMessages,
				parentmessages: result.parentMessages,
			},
			newer: { messages: newerMessages, parentmessages: [] },
		}
	} catch (e) {
		console.error(e)
	}
}

export const fetchHistory = async (payload, cancelToken) => {
	try {
		const data = await fetchHistoryWithoutStore(payload, cancelToken)

		if (!data.messages.length) return data

		if (payload.latest || payload.latestinclusive) {
			// need to move the start cursor to the oldest message
			const [start] = data.messages.slice(-1)
			setStartCursorForBubble(payload.bubble_id, start.id)
		} else if (payload.oldest || payload.oldestinclusive) {
			// need to move the stop cursor to the newest message
			const [stop] = data.messages
			setStopCursorForBubble(payload.bubble_id, stop.id)
		} else {
			// need to set both cursors
			const [start] = data.messages.slice(-1)
			const [stop] = data.messages
			// check current stop cursor in case new message came in while fetching
			const currentStop = getStopCursorForBubble(payload.bubble_id)
			setCursorsForBubble(payload.bubble_id, {
				start: start.id,
				stop: currentStop > stop.id ? currentStop : stop.id,
			})
		}
		setMessages(data.messages)
		setMessages(data.parentmessages)
		EventBus.$emit('bubbleHistoryComplete', payload.bubble_id)
		if (Object.keys(payload).length === 1 && payload.bubble_id) {
			// if getting fresh message list for this bubble, trim the cache
			deleteCachedMessagesNotInSet(data.messages)
		}
		return data
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const fetchMyMemberships = async (params = {}, config = {}) => {
	try {
		const id = getMyUserId()
		const rawMemberships = await getAllPages(
			`/users/${id}/memberships`,
			{
				'page[size]': 100,
				...params,
			},
			config
		)
		// this code loops through all memberships, gets unique records for chats, users, and
		// memberships, and then sets each batch in the store to avoid processing the same
		// data over and over again as that was a performance bottleneck
		const { chats, stats, users, memberships } = rawMemberships.reduce(
			(acc, { chat, user, stats, ...m }) => {
				acc.memberships[m.id] = m
				if (chat?.memberships?.length) {
					chat.memberships.forEach((m) => {
						acc.memberships[m.id] = m
					})
					delete chat.memberships
				}
				if (stats?.chat_id) acc.stats[stats.chat_id] = stats
				if (chat?.id) acc.chats[chat.id] = chat
				if (user?.id) acc.users[user.id] = user
				return acc
			},
			{ chats: {}, stats: {}, users: {}, memberships: {} }
		)
		setUsers(Object.values(users))
		setChats(Object.values(chats))
		setStats(Object.values(stats))
		setMembershipsSafe(Object.values(memberships))
		return rawMemberships
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const fetchMyMembershipForChat = async (chatId, params = {}) => {
	try {
		const myId = getMyUserId()
		params = {
			include: [
				'chat',
				'stats',
				'user',
				'chat.memberships.user',
				'chat.meeting',
				'chat.pinned_message',
				'chat.category',
				'chat.category.user_category',
				'chat.source_message',
			].join(','),
			...params,
		}
		const { data: membership } = await get(
			`/chats/${chatId}/memberships/${myId}`,
			params
		)
		const { chat, stats, user } = membership
		const { memberships, ...c } = chat
		setChat(c)
		setStat(stats)
		setUser(user)
		diffMembershipsForBubble(memberships)
		return membership
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const createChat = async (payload) => {
	try {
		const chat = await post('/chats', payload)
		setChat(chat)
		return chat
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const updateChat = async (
	chatId,
	payload,
	removeFromCategory = false
) => {
	try {
		const chat = await patch(`/chats/${chatId}`, payload)
		if (removeFromCategory) {
			removeChatFromCategory(chatId)
		}
		storeUpdateChat(chatId, chat)
		return chat
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const addUsersToChat = async (chatId, user_ids) => {
	try {
		const resp = await post(`/chats/${chatId}/memberships/batch`, {
			user_ids,
		})
		return resp
	} catch (e) {
		console.error(e)
		throw e
	}
}

export const createDM = async (members = []) => {
	try {
		const chat = await post('/chats/dms', { members })
		setChat(chat)
		return chat
	} catch (e) {
		console.error(e)
		throw e
	}
}
